// @version 1.8.4
// @updated 2026-06-21
// hk-metaobject-modal — メタオブジェクトカード → ?view={suffix} fetch → dialog 表示
//
// Changelog:
// - 1.8.4: 初回 open 時は dialog を先に開いてから Flickity 初期化（前後ナビ時のみ正常だった問題）
// - 1.8.3: モーダル注入後に Flickity syncLayout を遅延実行（stretch viewport 0px 対策）
// - 1.8.2: loading オーバーレイを dialog 直下に対応（padding 外まで覆う）
// - 1.8.1: 読み込み中も前後ナビを非表示にせず disabled のみ、差し替えは先挿入後削除
// - 1.8.0: 前後ナビ時は既存コンテンツを維持したオーバーレイ loading + フェード差し替え
// - 1.7.0: 一覧連動の前後ナビ（openNext/openPrev、data-hk-mo-modal-nav、端は非表示）
// - 1.6.4: Flickity 検出を hk-flickity-carousel タグに変更（js-flickity 廃止）
// - 1.6.3: 注入 HTML から script/link を除去、loadScriptOnce は head のみ参照（inert script 待ちでハングしない）
// - 1.6.2: HkFlickity.init を await（カスタム要素 upgrade 後に refresh）
// - 1.6.1: モーダル配色・幅・view は親 .hk-mo-cards の data-* のみ参照
// - 1.6.0: モーダル配色は一覧ブロック側のみ（テンプレート継承フォールバックを削除）
// - 1.5.4: Flickity 初期化前の二重 syncChrome を削除（init 内で syncChrome 済み）
// - 1.5.3: Flickity 初期化前後に syncChrome を呼び単一セル矢印を確実に非表示
// - 1.5.2: モーダル表示を Flickity 初期化より先に実行、loadScriptOnce の既読込判定修正、エラー時も dialog 表示
// - 1.5.1: HkFlickity.init(body) で js-flickity スキャン初期化に統一
// - 1.5.0: Flickity は親ページ読み込みを優先、未読み込み時のみ遅延取得してから refresh
// - 1.4.0: モーダル本文注入後に hk-flickity-carousel.refresh() でカルーセル再初期化
// - 1.3.0: モーダル fetch はテンプレート全体（data-hk-mo-modal-page）を取得
// - 1.2.0: 本実装（SRA 削除・modal_view suffix 対応）
// - 1.1.0: PoC — metaobject 代替テンプレート（?view=modal）を fetch

const MODAL_OPEN_SELECTOR = '[data-hk-mo-modal-open]';
const MODAL_NAV_SELECTOR = '[data-hk-mo-modal-nav]';
const MODAL_CARD_SELECTOR = '.hk-mo-cards__card, .card.hk-mo-carousel-card[data-carousel-card]';
const DEFAULT_MODAL_VIEW = 'modal';

/**
 * @param {HTMLElement} navRoot
 * @returns {HTMLElement[]}
 */
function getNavTriggers(navRoot) {
  if (!(navRoot instanceof HTMLElement)) return [];

  return Array.from(navRoot.querySelectorAll(`${MODAL_OPEN_SELECTOR}[data-metaobject-url]`)).filter((trigger) => {
    if (!(trigger instanceof HTMLElement)) return false;

    const card = trigger.closest(MODAL_CARD_SELECTOR);
    if (card instanceof HTMLElement && card.hidden) return false;

    return true;
  });
}

/**
 * @param {HTMLElement[]} triggers
 * @param {HTMLElement} trigger
 * @returns {number}
 */
function findTriggerIndex(triggers, trigger) {
  const handle = trigger.dataset.metaobjectHandle?.trim();
  const url = trigger.dataset.metaobjectUrl?.trim();

  const index = triggers.findIndex((candidate) => {
    if (!(candidate instanceof HTMLElement)) return false;
    if (handle && candidate.dataset.metaobjectHandle === handle) return true;
    if (url && candidate.dataset.metaobjectUrl === url) return true;
    return candidate === trigger;
  });

  return index;
}

/**
 * @param {HTMLElement} host
 */
function getDialogComponent(host) {
  const dialogComponent = host.querySelector('dialog-component');
  if (!(dialogComponent instanceof HTMLElement)) return null;
  return dialogComponent;
}

/**
 * @param {Document} doc
 * @returns {string}
 */
function extractModalDetailHtml(doc) {
  const page = doc.querySelector('[data-hk-mo-modal-page]');
  if (page instanceof HTMLElement) {
    return page.innerHTML.trim();
  }

  const layoutBody = doc.querySelector('body.hk-mo-modal-layout');
  if (layoutBody instanceof HTMLElement) {
    return layoutBody.innerHTML.trim();
  }

  const sections = doc.querySelectorAll('body .shopify-section');
  if (sections.length > 0) {
    return Array.from(sections)
      .map((section) => section.outerHTML)
      .join('');
  }

  const detail = doc.querySelector('[data-hk-mo-modal-detail]');
  if (detail instanceof HTMLElement) {
    return detail.outerHTML.trim();
  }

  return '';
}

/**
 * insertAdjacentHTML では script / link は実行・適用されない。loadScriptOnce の誤検出も防ぐ
 *
 * @param {string} html
 * @returns {string}
 */
function sanitizeInjectedModalHtml(html) {
  if (!html) return html;

  const doc = new DOMParser().parseFromString(`<div id="hk-mo-modal-root">${html}</div>`, 'text/html');
  const root = doc.getElementById('hk-mo-modal-root');
  if (!(root instanceof HTMLElement)) return html;

  root.querySelectorAll('script, link[rel="stylesheet"]').forEach((node) => node.remove());
  return root.innerHTML.trim();
}

/**
 * @param {string} src
 * @returns {HTMLScriptElement | null}
 */
function findHeadScript(src) {
  const match = document.head.querySelector(`script[src="${src}"]`);
  return match instanceof HTMLScriptElement ? match : null;
}

/**
 * @param {HTMLElement} trigger
 * @returns {HTMLElement | null}
 */
function getModalSourceRoot(trigger) {
  return trigger.closest('.hk-mo-cards');
}

/**
 * @param {HTMLElement} trigger
 * @returns {string}
 */
function getModalColorScheme(trigger) {
  return getModalSourceRoot(trigger)?.dataset.modalColorScheme || '';
}

/**
 * @param {HTMLElement} host
 * @param {string} colorScheme
 */
function applyDialogColorScheme(host, colorScheme) {
  if (!colorScheme) return;

  const dialog = host.querySelector('.hk-mo-modal__dialog');
  if (!(dialog instanceof HTMLElement)) return;

  dialog.classList.forEach((className) => {
    if (className.startsWith('color-')) dialog.classList.remove(className);
  });
  dialog.classList.add(`color-${colorScheme}`);
}

/**
 * @param {string} metaobjectUrl
 * @param {HTMLElement} host
 * @param {HTMLElement} trigger
 * @returns {URL}
 */
function buildModalFetchUrl(metaobjectUrl, host, trigger) {
  const current = new URL(window.location.href);
  const url = new URL(metaobjectUrl, window.location.origin);

  const previewThemeId = current.searchParams.get('preview_theme_id');
  if (previewThemeId) {
    url.searchParams.set('preview_theme_id', previewThemeId);
  }

  const sourceRoot = getModalSourceRoot(trigger);
  const modalView =
    sourceRoot?.dataset.modalView?.trim() ||
    host.dataset.modalView?.trim() ||
    DEFAULT_MODAL_VIEW;

  url.searchParams.set('view', modalView);

  return url;
}

/**
 * @param {HTMLElement} host
 * @param {HTMLElement} trigger
 */
function applyModalChrome(host, trigger) {
  const sourceRoot = getModalSourceRoot(trigger);
  const desktopWidth = sourceRoot?.dataset.modalWidthDesktop;
  const mobileWidth = sourceRoot?.dataset.modalWidthMobile;

  if (desktopWidth) {
    host.style.setProperty('--hk-mo-modal-width-desktop', desktopWidth);
  }
  if (mobileWidth) {
    host.style.setProperty('--hk-mo-modal-width-mobile', mobileWidth);
  }
}

/**
 * @param {string} href
 */
function loadStylesheetOnce(href) {
  if (!href || document.querySelector(`link[rel="stylesheet"][href="${href}"]`)) return;

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
}

/**
 * @param {HTMLScriptElement} script
 */
function markScriptLoaded(script) {
  script.dataset.hkLoaded = 'true';
}

/**
 * @param {HTMLScriptElement} script
 * @param {() => boolean} [isLoaded]
 * @returns {boolean}
 */
function isScriptAlreadyLoaded(script, isLoaded = () => false) {
  if (script.dataset.hkLoaded === 'true') return true;
  if (isLoaded()) return true;

  const readyState = script.readyState;
  return readyState === 'loaded' || readyState === 'complete';
}

/**
 * @param {string} src
 * @param {() => boolean} [isLoaded]
 * @returns {Promise<void>}
 */
function loadScriptOnce(src, isLoaded = () => false) {
  if (!src) return Promise.resolve();
  if (isLoaded()) return Promise.resolve();

  const existing = findHeadScript(src);
  if (existing) {
    if (isScriptAlreadyLoaded(existing, isLoaded)) {
      markScriptLoaded(existing);
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const onLoad = () => {
        markScriptLoaded(existing);
        resolve();
      };

      existing.addEventListener('load', onLoad, { once: true });
      existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.defer = true;
    script.addEventListener(
      'load',
      () => {
        markScriptLoaded(script);
        resolve();
      },
      { once: true }
    );
    script.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), { once: true });
    document.head.appendChild(script);
  });
}

/** @type {Promise<void> | null} */
let flickityAssetsPromise = null;

/**
 * 不足している Flickity 関連アセットだけ遅延読み込み
 *
 * @param {HTMLElement} host
 * @returns {Promise<void>}
 */
function ensureFlickityAssets(host) {
  const needsFlickity = typeof window.Flickity === 'undefined';
  const needsImagesLoaded = typeof window.imagesLoaded !== 'function';
  const needsCarousel = !customElements.get('hk-flickity-carousel');

  if (!needsFlickity && !needsImagesLoaded && !needsCarousel) {
    return Promise.resolve();
  }

  if (flickityAssetsPromise) return flickityAssetsPromise;

  const flickityCss = host.dataset.hkAssetFlickityCss || '';
  const carouselCss = host.dataset.hkAssetCarouselCss || '';
  const imagesLoadedJs = host.dataset.hkAssetImagesloadedJs || '';
  const flickityJs = host.dataset.hkAssetFlickityJs || '';
  const carouselJs = host.dataset.hkAssetCarouselJs || '';

  flickityAssetsPromise = (async () => {
    loadStylesheetOnce(flickityCss);
    loadStylesheetOnce(carouselCss);

    if (needsImagesLoaded && imagesLoadedJs) {
      await loadScriptOnce(imagesLoadedJs, () => typeof window.imagesLoaded === 'function');
    }

    if (needsFlickity && flickityJs) {
      await loadScriptOnce(flickityJs, () => typeof window.Flickity !== 'undefined');
    }

    if (needsCarousel && carouselJs) {
      await loadScriptOnce(carouselJs, () => !!customElements.get('hk-flickity-carousel'));
    }
  })().catch((error) => {
    flickityAssetsPromise = null;
    throw error;
  });

  return flickityAssetsPromise;
}

/**
 * @param {HTMLElement} host
 * @param {ParentNode} root
 * @returns {Promise<void>}
 */
async function initModalFlickityCarousels(host, root) {
  if (!root.querySelector('hk-flickity-carousel')) return;

  await ensureFlickityAssets(host);
  await window.HkFlickity?.init?.(root);
  scheduleModalFlickityLayoutSync(root);
}

/**
 * モーダル内カルーセル — メディア読込・表示後に stretch 高さを再同期
 *
 * @param {ParentNode} root
 */
function scheduleModalFlickityLayoutSync(root) {
  const sync = () => window.HkFlickity?.syncLayout?.(root);

  requestAnimationFrame(() => requestAnimationFrame(sync));
  window.setTimeout(sync, 300);
  window.setTimeout(sync, 900);
}

/**
 * dialog 表示後にレイアウトが確定するまで待つ
 *
 * @returns {Promise<void>}
 */
function waitForDialogLayout() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });
}

/**
 * @param {HTMLElement | null} dialogComponent
 */
function showModalDialog(dialogComponent) {
  if (dialogComponent && typeof dialogComponent.showDialog === 'function') {
    dialogComponent.showDialog();
    return;
  }

  const dialog = dialogComponent?.querySelector('dialog');
  if (dialog instanceof HTMLDialogElement && !dialog.open) {
    dialog.showModal();
  }
}

/**
 * @param {HTMLElement} body
 */
function clearModalBody(body) {
  Array.from(body.children).forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    node.remove();
  });
}

/**
 * 旧コンテンツを残したまま新 HTML を挿入してから削除（ナビの高さジャンプ防止）
 *
 * @param {HTMLElement} body
 * @param {string} detailHtml
 */
function replaceModalBodyContent(body, detailHtml) {
  if (!detailHtml) {
    clearModalBody(body);
    return;
  }

  const wrapper = document.createElement('div');
  wrapper.innerHTML = sanitizeInjectedModalHtml(detailHtml);

  const oldNodes = Array.from(body.children).filter((node) => node instanceof HTMLElement);

  Array.from(wrapper.childNodes).forEach((node) => {
    body.appendChild(node);
  });

  oldNodes.forEach((node) => {
    node.remove();
  });
}

/**
 * @param {HTMLElement} host
 * @returns {HTMLDialogElement | null}
 */
function getOpenDialog(host) {
  const dialog = host.querySelector('dialog[open]');
  return dialog instanceof HTMLDialogElement ? dialog : null;
}

/**
 * @param {HTMLElement} body
 * @returns {HTMLElement | null}
 */
function getModalDialog(body) {
  const dialog = body.closest('.hk-mo-modal__dialog');
  return dialog instanceof HTMLElement ? dialog : null;
}

/**
 * @param {HTMLElement} body
 * @param {{ keepContent?: boolean }} [options]
 */
function showLoadingOverlay(body, { keepContent = false } = {}) {
  const dialog = getModalDialog(body);
  const overlay = dialog?.querySelector('[data-hk-mo-modal-overlay]');
  if (!(dialog instanceof HTMLElement) || !(overlay instanceof HTMLElement)) return;

  if (keepContent && body.offsetHeight > 0) {
    body.style.minHeight = `${body.offsetHeight}px`;
  } else if (!keepContent) {
    clearModalBody(body);
  }

  dialog.classList.add('hk-mo-modal__dialog--loading');
  overlay.hidden = false;
  overlay.setAttribute('aria-hidden', 'false');
  overlay.classList.remove('is-visible');

  requestAnimationFrame(() => {
    requestAnimationFrame(() => overlay.classList.add('is-visible'));
  });
}

/**
 * @param {HTMLElement} body
 * @returns {Promise<void>}
 */
function hideLoadingOverlay(body) {
  const dialog = getModalDialog(body);
  const overlay = dialog?.querySelector('[data-hk-mo-modal-overlay]');
  dialog?.classList.remove('hk-mo-modal__dialog--loading');

  if (!(overlay instanceof HTMLElement)) {
    body.style.minHeight = '';
    return Promise.resolve();
  }

  overlay.classList.remove('is-visible');
  overlay.setAttribute('aria-hidden', 'true');

  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      overlay.hidden = true;
      body.style.minHeight = '';
      resolve();
    };

    overlay.addEventListener('transitionend', (event) => {
      if (event.target === overlay && event.propertyName === 'opacity') finish();
    }, { once: true });

    window.setTimeout(finish, 320);
  });
}

/**
 * @param {HTMLElement} body
 */
function revealModalContent(body) {
  const target =
    body.querySelector('[data-hk-mo-modal-page], [data-hk-mo-modal-detail], .hk-mo-modal-detail__inner') ||
    body.querySelector(':scope > *');

  if (!(target instanceof HTMLElement)) return;

  target.classList.add('hk-mo-modal__swap');
  requestAnimationFrame(() => {
    requestAnimationFrame(() => target.classList.add('is-visible'));
  });
}

/**
 * @param {HTMLElement} host
 * @param {string} title
 */
function setModalTitle(host, title) {
  const titleEl = host.querySelector('[data-hk-mo-modal-title]');
  if (!(titleEl instanceof HTMLElement)) return;
  titleEl.textContent = title;
  titleEl.hidden = !title;
}

class MetaobjectModal extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {HTMLElement | null} */
  #navRoot = null;

  /** @type {number} */
  #currentIndex = -1;

  /** @type {boolean} */
  #isLoading = false;

  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onDocumentKeyDown);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onDocumentKeyDown);
    this.#abortController?.abort();
  }

  /** @returns {HTMLElement[]} */
  #getNavTriggers() {
    return getNavTriggers(/** @type {HTMLElement} */ (this.#navRoot));
  }

  /**
   * @param {ParentNode} root
   */
  #syncModalNav(root) {
    const triggers = this.#getNavTriggers();
    const hasNav = this.#navRoot instanceof HTMLElement && triggers.length > 1;
    const hasPrev = hasNav && this.#currentIndex > 0;
    const hasNext = hasNav && this.#currentIndex >= 0 && this.#currentIndex < triggers.length - 1;

    root.querySelectorAll(`${MODAL_NAV_SELECTOR}[data-hk-mo-modal-nav="prev"]`).forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      node.hidden = !hasPrev;
      if (node instanceof HTMLButtonElement) {
        node.disabled = !hasPrev || this.#isLoading;
      }
    });

    root.querySelectorAll(`${MODAL_NAV_SELECTOR}[data-hk-mo-modal-nav="next"]`).forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      node.hidden = !hasNext;
      if (node instanceof HTMLButtonElement) {
        node.disabled = !hasNext || this.#isLoading;
      }
    });

    root.querySelectorAll('[data-hk-mo-modal-nav-group]').forEach((group) => {
      if (!(group instanceof HTMLElement)) return;

      const prevBtn = group.querySelector(`${MODAL_NAV_SELECTOR}[data-hk-mo-modal-nav="prev"]`);
      const nextBtn = group.querySelector(`${MODAL_NAV_SELECTOR}[data-hk-mo-modal-nav="next"]`);
      const prevHidden = prevBtn instanceof HTMLElement ? prevBtn.hidden : true;
      const nextHidden = nextBtn instanceof HTMLElement ? nextBtn.hidden : true;
      group.hidden = prevHidden && nextHidden;
      group.classList.toggle('hk-mo-modal-nav--busy', this.#isLoading);
      group.setAttribute('aria-busy', this.#isLoading ? 'true' : 'false');
    });
  }

  /**
   * @param {HTMLElement} trigger
   */
  #updateNavState(trigger) {
    const sourceRoot = getModalSourceRoot(trigger);
    if (sourceRoot instanceof HTMLElement) {
      this.#navRoot = sourceRoot;
    }

    if (!(this.#navRoot instanceof HTMLElement)) {
      this.#currentIndex = -1;
      return;
    }

    const triggers = this.#getNavTriggers();
    this.#currentIndex = findTriggerIndex(triggers, trigger);
  }

  openNext() {
    if (this.#isLoading) return;

    const triggers = this.#getNavTriggers();
    if (this.#currentIndex < 0 || this.#currentIndex >= triggers.length - 1) return;

    const nextTrigger = triggers[this.#currentIndex + 1];
    if (nextTrigger instanceof HTMLElement) {
      this.#openFromTrigger(nextTrigger);
    }
  }

  openPrev() {
    if (this.#isLoading) return;

    const triggers = this.#getNavTriggers();
    if (this.#currentIndex <= 0) return;

    const prevTrigger = triggers[this.#currentIndex - 1];
    if (prevTrigger instanceof HTMLElement) {
      this.#openFromTrigger(prevTrigger);
    }
  }

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const nav = event.target.closest(MODAL_NAV_SELECTOR);
    if (nav instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();

      const action = nav.dataset.hkMoModalNav;
      if (action === 'prev') this.openPrev();
      else if (action === 'next') this.openNext();
      return;
    }

    const trigger = event.target.closest(MODAL_OPEN_SELECTOR);
    if (!trigger || !(trigger instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();
    this.#openFromTrigger(trigger);
  };

  /** @param {KeyboardEvent} event */
  #onDocumentKeyDown = (event) => {
    const dialog = this.querySelector('dialog[open]');
    if (!(dialog instanceof HTMLDialogElement)) return;

    const target = event.target;
    if (
      target instanceof HTMLElement &&
      target.closest('input, textarea, select, [contenteditable="true"]')
    ) {
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.openPrev();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.openNext();
    }
  };

  /** @param {HTMLElement} trigger */
  async #openFromTrigger(trigger) {
    const metaobjectUrl = trigger.dataset.metaobjectUrl?.trim();

    applyModalChrome(this, trigger);
    setModalTitle(this, trigger.dataset.modalTitle || '');
    applyDialogColorScheme(this, getModalColorScheme(trigger));

    const body = this.querySelector('[data-hk-mo-modal-body]');
    const dialogComponent = getDialogComponent(this);
    if (!(body instanceof HTMLElement) || !dialogComponent) return;

    const isAlreadyOpen = getOpenDialog(this) != null;

    this.#updateNavState(trigger);

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#isLoading = true;
    this.#syncModalNav(body);
    showLoadingOverlay(body, { keepContent: isAlreadyOpen });

    if (!isAlreadyOpen) {
      showModalDialog(dialogComponent);
      await waitForDialogLayout();
    }

    if (!metaobjectUrl) {
      this.#isLoading = false;
      body.insertAdjacentHTML(
        'beforeend',
        '<p class="hk-mo-modal-detail__empty">モーダル表示にはメタオブジェクトのストアフロント URL が必要です。管理画面で Web ページを有効にするか、カードのリンク URL パターンを設定してください。</p>'
      );
      revealModalContent(body);
      await hideLoadingOverlay(body);
      showModalDialog(dialogComponent);
      this.#syncModalNav(body);
      return;
    }

    try {
      const url = buildModalFetchUrl(metaobjectUrl, this, trigger);
      const response = await fetch(url.toString(), {
        signal,
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      });
      if (signal.aborted) return;

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const html = await response.text();
      if (signal.aborted) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const detailHtml = extractModalDetailHtml(doc);

      if (!detailHtml) {
        clearModalBody(body);
        body.insertAdjacentHTML(
          'beforeend',
          '<p class="hk-mo-modal-detail__empty">コンテンツを読み込めませんでした。（?view=modal テンプレートを確認してください）</p>'
        );
      } else {
        replaceModalBodyContent(body, detailHtml);
      }

      this.#syncModalNav(body);

      if (!trigger.dataset.modalTitle) {
        const autoTitle = body.querySelector('.hk-mo-modal-field__title');
        if (autoTitle?.textContent) {
          setModalTitle(this, autoTitle.textContent.trim());
        }
      }

      if (detailHtml) {
        if (!isAlreadyOpen) {
          revealModalContent(body);
          await waitForDialogLayout();
        }

        try {
          await initModalFlickityCarousels(this, body);
        } catch (flickityError) {
          console.error('hk-metaobject-modal flickity', flickityError);
        }
      }

      if (signal.aborted) return;

      if (isAlreadyOpen) {
        revealModalContent(body);
      }
      await hideLoadingOverlay(body);

      this.#isLoading = false;
      if (isAlreadyOpen) {
        showModalDialog(dialogComponent);
      }
      scheduleModalFlickityLayoutSync(body);
      this.#syncModalNav(body);
    } catch (error) {
      if (signal.aborted) return;

      clearModalBody(body);
      body.insertAdjacentHTML(
        'beforeend',
        '<p class="hk-mo-modal-detail__empty">読み込みに失敗しました。</p>'
      );
      revealModalContent(body);
      await hideLoadingOverlay(body);

      this.#isLoading = false;
      showModalDialog(dialogComponent);
      this.#syncModalNav(body);
      console.error('hk-metaobject-modal', error);
    }
  }
}

if (!customElements.get('hk-metaobject-modal')) {
  customElements.define('hk-metaobject-modal', MetaobjectModal);
}

/**
 * @returns {MetaobjectModal | null}
 */
function getMetaobjectModalHost() {
  const host = document.querySelector('hk-metaobject-modal');
  return host instanceof MetaobjectModal ? host : null;
}

window.HkMoModal = {
  openNext: () => getMetaobjectModalHost()?.openNext(),
  openPrev: () => getMetaobjectModalHost()?.openPrev(),
};
